import { ThunkAction, ThunkDispatch } from 'redux-thunk'
import { AnyAction } from 'redux'
import { RootState } from 'src/Store'
import { AddressReverse, AddressReverseResult } from 'src/dataSources/dataforsyningen/AddressReverse'
import { EmptyAction, DataAction, MessageAction } from 'src/CustomTypes'
import { VirtualBorehole } from 'src/dataSources/geo/VirtualBorehole'
import { getSelectedHydroModelLayers } from 'src/utils/MapLayerUtils'

export const FETCHING_ADDRESS_STARTED = 'VIRTUAL_BOREHOLE_INFO_ACTIONS_FETCHING_ADDRESS_STARTED'
export const FETCHING_ADDRESS_SUCCESS = 'VIRTUAL_BOREHOLE_INFO_ACTIONS_FETCHING_ADDRESS_SUCCESS'
export const FETCHING_ADDRESS_FAILURE = 'VIRTUAL_BOREHOLE_INFO_ACTIONS_FETCHING_ADDRESS_FAILURE'
export const FETCHING_IMAGE_STARTED = 'VIRTUAL_BOREHOLE_INFO_ACTIONS_FETCHING_IMAGE_STARTED'
export const FETCHING_IMAGE_SUCCESS = 'VIRTUAL_BOREHOLE_INFO_ACTIONS_FETCHING_IMAGE_SUCCESS'
export const FETCHING_IMAGE_FAILURE = 'VIRTUAL_BOREHOLE_INFO_ACTIONS_FETCHING_IMAGE_FAILURE'
export const SET_MAX_DEPTH = 'VIRTUAL_BOREHOLE_INFO_ACTIONS_SET_MAX_DEPTH'
export const SET_COORDINATES = 'VIRTUAL_BOREHOLE_INFO_ACTIONS_SET_COORDINATES'
export const CLEAR_ADDRESS_STATE = 'VIRTUAL_BOREHOLE_INFO_ACTIONS_CLEAR_ADDRESS_STATE'
export const CLEAR_IMAGE_STATE = 'VIRTUAL_BOREHOLE_INFO_ACTIONS_CLEAR_IMAGE_STATE'
export const CLEAR_STATE = 'VIRTUAL_BOREHOLE_INFO_ACTIONS_CLEAR_STATE'

interface VirtualBoreholeInfoActions {
  fetchingAddressStarted: () => EmptyAction
  fetchingAddressFailure: (message: string) => MessageAction
  fetchingAddressSuccess: (address: AddressReverseResult) => DataAction<AddressReverseResult>
  fetchingImageStarted: () => EmptyAction
  fetchingImageFailure: (message: string) => MessageAction
  fetchingImageSuccess: (image: string) => DataAction<string>
  setMaxDepth: (maxDepth: number) => DataAction<number>
  clearAddressState: () => EmptyAction
  clearImageState: () => EmptyAction
  clearState: () => EmptyAction

  // Thunk actions.
  setCoordinates: (coords: number[]) => DataAction<number[]>
  updateVirtualBorehole: (coords: number[]) => ThunkAction<Promise<void>, RootState, {}, AnyAction>
}

export const virtualBoreholeInfoActions: VirtualBoreholeInfoActions = {
  fetchingAddressStarted: (): EmptyAction => {
    return {
      type: FETCHING_ADDRESS_STARTED
    }
  },
  fetchingAddressFailure: (message: string): MessageAction => {
    return {
      type: FETCHING_ADDRESS_FAILURE,
      message: message
    }
  },
  fetchingAddressSuccess: (address: AddressReverseResult): DataAction<AddressReverseResult> => {
    return {
      type: FETCHING_ADDRESS_SUCCESS,
      data: address
    }
  },
  fetchingImageStarted: (): EmptyAction => {
    return {
      type: FETCHING_IMAGE_STARTED
    }
  },
  fetchingImageFailure: (message: string) => {
    return {
      type: FETCHING_IMAGE_FAILURE,
      message: message
    }
  },
  fetchingImageSuccess: (image: string) => {
    return {
      type: FETCHING_IMAGE_SUCCESS,
      data: image
    }
  },
  setMaxDepth: (maxDepth: number) => {
    return {
      type: SET_MAX_DEPTH,
      data: maxDepth
    }
  },
  setCoordinates: (coords: number[]) => {
    return {
      type: SET_COORDINATES,
      data: coords
    }
  },
  clearAddressState: () => {
    return {
      type: CLEAR_ADDRESS_STATE
    }
  },
  clearImageState: () => {
    return {
      type: CLEAR_IMAGE_STATE
    }
  },
  clearState: () => {
    return {
      type: CLEAR_STATE
    }
  },
  updateVirtualBorehole: (coords: number[]): ThunkAction<Promise<void>, RootState, {}, AnyAction> => {
    return async (dispatch: ThunkDispatch<RootState, {}, AnyAction>, getState: () => RootState): Promise<void> => {
      // Get current coordinates
      const currentCoords = getState().virtualBoreholeInfoState.coordinates

      // Clear current image state.
      dispatch(virtualBoreholeInfoActions.clearImageState())

      // Set new coordinates
      dispatch(virtualBoreholeInfoActions.setCoordinates(coords))

      // --- Postal address ---
      if (currentCoords !== coords) {
        dispatch(virtualBoreholeInfoActions.clearAddressState())
        const wsAddressReverse = new AddressReverse()
        wsAddressReverse.setCoords(coords)
        dispatch(virtualBoreholeInfoActions.fetchingAddressStarted())
        wsAddressReverse.fetchReverseAddress()
          .then((addressInfo: AddressReverseResult) => {
            dispatch(virtualBoreholeInfoActions.fetchingAddressSuccess(addressInfo))
          })
          .catch(err => {
            console.error(err)
            const errorMessage: string = (err as Error).message
            dispatch(virtualBoreholeInfoActions.fetchingAddressFailure(errorMessage))
          })
      }

      // --- Virtual Borehole image ---
      const token = getState().authState.token
      const maxDepth = getState().virtualBoreholeInfoState.maxDepth
      const modelId = getState().generalState.selectedGeoModel?.ID
      const wsVirtualBorehole = new VirtualBorehole(token)
      wsVirtualBorehole.setCoordinate(coords)
      wsVirtualBorehole.setMaxDepth(maxDepth)
      wsVirtualBorehole.setModelId(modelId !== undefined ? modelId : '')
      wsVirtualBorehole.setHydroModelIds(getSelectedHydroModelLayers(getState().mapState))
      dispatch(virtualBoreholeInfoActions.fetchingImageStarted())
      wsVirtualBorehole.fetchVirtualBorehole()
        .then(image => {
          dispatch(virtualBoreholeInfoActions.fetchingImageSuccess(image))
        })
        .catch(err => {
          console.error(err)
          const errorMessage: string = (err as Error).message
          dispatch(virtualBoreholeInfoActions.fetchingImageFailure(errorMessage))
        })
    }
  }
}
